import { zonedTimeToUtc } from './storage-terms.js';

/**
 * Vague 7 : heures et jours **locaux** du garage, sans dépendance (Intl). Utilisé par les indicateurs de
 * priorité, les horaires d'ouverture, les chiffres de l'atelier et le bilan hebdomadaire.
 */

export interface LocalParts {
  y: number;
  /** 1 à 12. */
  m: number;
  d: number;
  h: number;
  min: number;
  /** 0 = dimanche … 6 = samedi. */
  weekday: number;
}

export function localParts(instant: Date, timeZone: string): LocalParts {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(instant);
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value);
  const y = get('year');
  const m = get('month');
  const d = get('day');
  return { y, m, d, h: get('hour'), min: get('minute'), weekday: new Date(Date.UTC(y, m - 1, d)).getUTCDay() };
}

const pad = (n: number) => String(n).padStart(2, '0');

/** Jour local `YYYY-MM-DD` d'un instant dans le fuseau. */
export function localDateKey(instant: Date, timeZone: string): string {
  const p = localParts(instant, timeZone);
  return `${p.y}-${pad(p.m)}-${pad(p.d)}`;
}

function parseKey(key: string): { y: number; m: number; d: number } {
  const [y, m, d] = key.split('-').map(Number) as [number, number, number];
  return { y, m, d };
}

/** Ajoute des jours calendaires à un jour local. */
export function addDaysToKey(key: string, days: number): string {
  const { y, m, d } = parseKey(key);
  const t = new Date(Date.UTC(y, m - 1, d + days));
  return `${t.getUTCFullYear()}-${pad(t.getUTCMonth() + 1)}-${pad(t.getUTCDate())}`;
}

/** Nombre de jours calendaires de `from` à `to` (jours locaux). */
export function daysBetweenKeys(from: string, to: string): number {
  const a = parseKey(from);
  const b = parseKey(to);
  return Math.round((Date.UTC(b.y, b.m - 1, b.d) - Date.UTC(a.y, a.m - 1, a.d)) / 86_400_000);
}

/** Jour de la semaine d'un jour local (0 = dimanche). */
export function weekdayOfKey(key: string): number {
  const { y, m, d } = parseKey(key);
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}

/** Instant UTC du début d'un jour local (minuit, changements d'heure gérés). */
export function startOfLocalDay(key: string, timeZone: string): Date {
  const { y, m, d } = parseKey(key);
  return zonedTimeToUtc({ y, m, d, h: 0, min: 0, s: 0, ms: 0 }, timeZone);
}

/** Instant UTC d'une heure locale `HH:MM` d'un jour local. */
export function localTimeToUtc(key: string, hhmm: string, timeZone: string): Date {
  const { y, m, d } = parseKey(key);
  const [h, min] = hhmm.split(':').map(Number) as [number, number];
  return zonedTimeToUtc({ y, m, d, h, min, s: 0, ms: 0 }, timeZone);
}

/** Lundi (jour local) de la semaine qui contient `key`. */
export function mondayOfKey(key: string): string {
  const wd = weekdayOfKey(key);
  return addDaysToKey(key, -((wd + 6) % 7));
}

/** Mois local `YYYY-MM` d'un instant. */
export function localMonthKey(instant: Date, timeZone: string): string {
  return localDateKey(instant, timeZone).slice(0, 7);
}

/** Mois `YYYY-MM` décalé de `delta` mois. */
export function addMonthsToMonthKey(month: string, delta: number): string {
  const [y, m] = month.split('-').map(Number) as [number, number];
  const t = new Date(Date.UTC(y, m - 1 + delta, 1));
  return `${t.getUTCFullYear()}-${pad(t.getUTCMonth() + 1)}`;
}
